import { DateTime } from 'luxon';

import { accountSummaryFormatters } from '@opengov/reporting-formatters/formatters/accountSummaryFormatters';
import { calendarUnitFormatters } from '@opengov/reporting-formatters/formatters/calendarUnitFormatters';
import { CalendarUnit, IAccountSummaryFormatting } from '@opengov/reporting-types';

import { CellFormatter, EColumnType } from './types';

const removeNegativeSignIfRoundedValueZero = (value: string, uptoDecimalVal: number, type: EColumnType) => {
  const isPercentType = type === EColumnType.ClassicPercent || type === EColumnType.Percentage;
  //As we are considering values to only two decimals so array contains 0 till two decimals
  const negativeZeroStrings = ['-0', '-0.0', '-0.00'];
  const parsedValue = parseFloat(isPercentType ? `${Number(value) * 100}` : value).toFixed(uptoDecimalVal);
  if (negativeZeroStrings.includes(parsedValue)) {
    return 0;
  }
  return parseFloat(value);
};

/** A cell with no data renders blank: a missing, null or empty value must never read as a zero. */
export const isMissingCellValue = (value: unknown): boolean => value === undefined || value === null || value === '';

export const cellFormatter: CellFormatter = (
  value: string | number | null | undefined,
  format?: EColumnType,
  calendarUnit?: CalendarUnit
): string => {
  if (isMissingCellValue(value)) {
    return '';
  }
  const cellValue = String(value);
  switch (format) {
    case EColumnType.Currency: {
      const formattedCellValue = removeNegativeSignIfRoundedValueZero(cellValue, 0, format);
      return isNaN(formattedCellValue)
        ? ''
        : new Intl.NumberFormat('en-US', {
            style: EColumnType.Currency,
            currency: 'USD',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
          })
            .format(formattedCellValue)
            .replace('(', '-') // Avoid wrapping negative currency value in () for IE.
            .replace(')', '');
    }
    case EColumnType.Numeric: {
      const formattedCellValue = removeNegativeSignIfRoundedValueZero(cellValue, 2, format);
      return isNaN(formattedCellValue)
        ? ''
        : new Intl.NumberFormat('en-US', {
            style: 'decimal',
            // Follow the reporting numeric format
            maximumFractionDigits: 3,
            minimumFractionDigits: 0
          }).format(formattedCellValue);
    }
    case EColumnType.ClassicPercent:
    case EColumnType.Percentage: {
      const formattedCellValue = removeNegativeSignIfRoundedValueZero(cellValue, 1, format);
      return isNaN(formattedCellValue)
        ? ''
        : new Intl.NumberFormat('en-US', {
            style: EColumnType.ClassicPercent,
            minimumFractionDigits: 0,
            maximumFractionDigits: 1
          }).format(formattedCellValue);
    }
    case EColumnType.Integer: {
      const formattedCellValue = removeNegativeSignIfRoundedValueZero(cellValue, 0, format);
      return isNaN(formattedCellValue)
        ? ''
        : new Intl.NumberFormat('en-US', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(formattedCellValue);
    }
    case EColumnType.Date: {
      return DateTime.fromSQL(cellValue).invalidReason ? '' : DateTime.fromSQL(cellValue).toLocaleString();
    }

    default:
      // Should move this date formatting around to look more consistent with how other formatting is set up
      let formattedCalendarValue;
      if (calendarUnit && calendarUnit !== CalendarUnit.FiscalQuarter) {
        formattedCalendarValue = calendarUnitFormatters[calendarUnit].formatter()(cellValue);
      }
      return formattedCalendarValue ?? cellValue;
  }
};

export const formatWithAccountSummaryFormatter = (
  accountSummaryTableFormatting: IAccountSummaryFormatting,
  value: string | number | null | undefined,
  format?: EColumnType,
  calendarUnit?: CalendarUnit
): string => {
  switch (format) {
    case EColumnType.Currency: {
      return accountSummaryFormatters(accountSummaryTableFormatting).currency(value);
    }
    case EColumnType.Percentage: {
      return accountSummaryFormatters(accountSummaryTableFormatting).percent(value);
    }
    case EColumnType.Numeric: {
      return accountSummaryFormatters(accountSummaryTableFormatting).number(value);
    }
    default:
      // Currently there is only Currency, Percentage and Numeric values allowed on MDS report
      //... All other format will be handled by previous embed formatter to keep it consistent
      return cellFormatter(value, format, calendarUnit);
  }
};

export const createAccountSummaryFormatter = (formatting: IAccountSummaryFormatting): CellFormatter => {
  return (value, format, calendarUnit) => formatWithAccountSummaryFormatter(formatting, value, format, calendarUnit);
};
